import { MobilityProductExtraAttributesType } from 'screens/AccessExpressV3/accessExpress.types';
import {
  DeepPartial,
  DotNotation,
  GlobalZoneEnum,
  OneLevelNestedKeys,
  SelectedAttributes,
  YesNoNullable,
} from './mobility.types';

export const getInitialParent = <K extends keyof SelectedAttributes>(
  key: K,
): NonNullable<SelectedAttributes[K]> => {
  const val = initialSelected[key];
  if (val === null) {
    throw new Error(`initialSelected[${String(key)}] is null!`);
  }
  return val as NonNullable<SelectedAttributes[K]>;
};

export const initialSelected: SelectedAttributes = {
  chooseVariant: null,
  choosePricingOption: null,
  carrier: null,
  simType: null,
  dataPlanNeeded: {
    neededPlan: null,
    activation: null,
  },
  simOption: null,
  planType: null,
  planTypeOption: null,
  global: {
    scope: null,
    globalZone: null,
    coverage: null,
    orderCarrier: ['AT&T', 'T-Mobile', 'Verizon'],
  },
  secondaryPlan: null,
};

export const resetToDefaultBasedOnKey = {
  chooseVariant: [
    'choosePricingOption',
    'carrier',
    'dataPlanNeeded',
    'simOption',
    'simType',
    'planType',
    'planTypeOption',
    'global',
    'secondaryPlan',
  ],
  'dataPlanNeeded.activation': [
    'carrier',
    'simOption',
    'simType',
    'planType',
    'planTypeOption',
    'global',
  ],
  'dataPlanNeeded.neededPlan': [
    { parentKey: 'dataPlanNeeded', keys: ['activation'] },
    'carrier',
    'simOption',
    'simType',
    'planType',
    'planTypeOption',
    'global',
  ],
  choosePricingOption: [
    'carrier',
    'simOption',
    'simType',
    'planType',
    'planTypeOption',
    'global',
    'secondaryPlan',
  ],
  carrier: [
    'simType',
    'simOption',
    'planType',
    'planTypeOption',
    'global',
    'secondaryPlan',
  ],
  simType: ['planType', 'planTypeOption', 'global', 'secondaryPlan'],
  simOptions: [],
  planType: ['planTypeOption', 'secondaryPlan'],
  'global.scope': [
    { parentKey: 'global', keys: ['coverage', 'globalZone', 'orderCarrier'] },
    'planTypeOption',
    'secondaryPlan',
  ],
  'global.coverage': [
    { parentKey: 'global', keys: ['orderCarrier'] },
    'secondaryPlan',
  ],
  'global.globalZone': ['planTypeOption', 'secondaryPlan'],
  'global.orderCarrier': ['secondaryPlan'],
  planTypeOptions: ['secondaryPlan'],
  secondaryPlan: [],
};

export const resetKeysToDefault = (
  key: DotNotation<SelectedAttributes>,
  prev: SelectedAttributes,
  value: DeepPartial<SelectedAttributes>,
  keysToReset?: (
    | keyof SelectedAttributes
    | {
        parentKey: keyof SelectedAttributes;
        keys: OneLevelNestedKeys<SelectedAttributes>[];
      }
  )[],
) => {
  const _keysToReset =
    keysToReset ??
    (resetToDefaultBasedOnKey[key as keyof typeof resetToDefaultBasedOnKey] ||
      []);

  return _keysToReset.reduce((acc, resetKey) => {
    // Handle grouped nested keys
    if (typeof resetKey === 'object') {
      const { parentKey, keys } = resetKey as {
        parentKey: keyof SelectedAttributes;
        keys: string[];
      };
      let initialParent: NonNullable<SelectedAttributes[typeof parentKey]>;
      try {
        initialParent = getInitialParent(parentKey);
      } catch {
        return {
          ...acc,
          [parentKey]: prev[parentKey],
        };
      }

      let resetData = {};
      keys.forEach((key) => {
        resetData = {
          ...resetData,
          [key]: initialParent[key as keyof typeof initialParent],
        };
      });

      return {
        ...acc,
        [parentKey]: {
          ...(prev[parentKey] as object),
          ...(value[parentKey] as object),
          ...resetData,
        },
      };
    }

    return {
      ...acc,
      [resetKey]: initialSelected[resetKey as keyof SelectedAttributes],
    };
  }, {} as Partial<SelectedAttributes>);
};

export const findSimOption = (
  data: MobilityProductExtraAttributesType,
  simOptionId: number,
) => {
  let sim: Omit<
    SelectedAttributes,
    'chooseVariant' | 'choosePricingOption' | 'dataPlanNeeded'
  > = {} as Omit<SelectedAttributes, 'chooseVariant' | 'choosePricingOption'>;
  for (const carrier of data?.carriers || []) {
    for (const simType of carrier.sim_types) {
      const simOption = simType.sim_options.find(
        (so) => so.pim_product_pricing_id === simOptionId,
      );
      if (simOption) {
        sim = {
          ...sim,
          carrier,
          simType,
          simOption,
        };
        break;
      }
    }
    if (sim.simOption) break;
  }
  return sim;
};

export const findPlanTypeOption = (
  data: MobilityProductExtraAttributesType,
  planTypeOptionId: number,
) => {
  let sim: Omit<
    SelectedAttributes,
    'chooseVariant' | 'choosePricingOption' | 'dataPlanNeeded'
  > = {} as Omit<SelectedAttributes, 'chooseVariant' | 'choosePricingOption'>;
  for (const carrier of data?.carriers || []) {
    for (const simType of carrier.sim_types) {
      for (const planType of simType.plan_types) {
        const planTypeOption = planType.plan_options.find(
          (po) => po.pim_product_pricing_id === planTypeOptionId,
        );
        if (planTypeOption) {
          sim = {
            ...sim,
            carrier,
            simType,
            planType,
            planTypeOption,
          };
          break;
        }
      }
      if (sim.planTypeOption) break;
    }
    if (sim.planTypeOption) break;
  }
  return sim;
};

export const findGlobal = (
  sim: Omit<
    SelectedAttributes,
    'chooseVariant' | 'choosePricingOption' | 'dataPlanNeeded'
  >,
  globalCoverage: string,
  globalOrderCarrier: string,
) => {
  const scope =
    sim.planTypeOption?.product_alias === null ? 'domestic' : 'global';
  const globalZone =
    scope === 'global' && sim.planTypeOption?.product_alias
      ? Object.keys(GlobalZoneEnum).find(
          (key) =>
            GlobalZoneEnum[key as keyof typeof GlobalZoneEnum] ===
            sim.planTypeOption?.product_alias,
        )
      : null;

  return {
    scope,
    globalZone: globalZone as keyof typeof GlobalZoneEnum | null,
    coverage: (globalCoverage as YesNoNullable) ?? null,
    orderCarrier:
      (globalOrderCarrier?.split(',') as ('AT&T' | 'T-Mobile' | 'Verizon')[]) ||
      initialSelected.global.orderCarrier,
  } as SelectedAttributes['global'];
};

export const getSimTypeOptionsByState = (
  state: SelectedAttributes,
  data: MobilityProductExtraAttributesType | undefined,
) => {
  const _simTypeOptions =
    (state.carrier?.sim_types || data?.carriers[0]?.sim_types) ?? [];
  return _simTypeOptions.filter((el) => el.sim_options.length);
};

export const getPlanTypeOptionsByState = (
  state: SelectedAttributes,
  data: MobilityProductExtraAttributesType | undefined,
) => {
  if (state.carrier?.carrier_name === 'Granite Multi Carrier SIM') {
    if (state.global.scope === 'domestic')
      return (
        state.planType?.plan_options.filter(
          (option) => option.product_alias === null,
        ) ?? []
      );
    else if (state.global.scope === 'global') {
      if (state.global.globalZone !== null) {
        return (
          state.planType?.plan_options.filter(
            (option) =>
              option.product_alias &&
              option.product_alias.includes(
                state.global.globalZone
                  ? GlobalZoneEnum[state.global.globalZone]
                  : '',
              ),
          ) ?? []
        );
      }
    }
    return [];
  } else
    return (
      (state.planType?.plan_options ||
        state.simType?.plan_types[0]?.plan_options ||
        data?.carriers[0]?.sim_types[0]?.plan_types[0]?.plan_options) ??
      []
    );
};
